import React from 'react';
import { AlertTriangle, AlertCircle, Info, CheckCircle2, Clock } from 'lucide-react';

export type PriorityLevel = 'critical' | 'high' | 'medium' | 'low';
export type StatusType = 'resolved' | 'unresolved' | 'investigating';

interface PriorityBadgeProps {
    priority: PriorityLevel;
    className?: string;
    showIcon?: boolean;
}

export const PriorityBadge: React.FC<PriorityBadgeProps> = ({
                                                                priority,
                                                                className = '',
                                                                showIcon = true
                                                            }) => {
    const getPriorityConfig = () => {
        switch (priority) {
            case 'critical':
                return {
                    label: 'Critical',
                    icon: AlertTriangle,
                    classes: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-500/30',
                };
            case 'high':
                return {
                    label: 'High',
                    icon: AlertCircle,
                    classes: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-500/30',
                };
            case 'medium':
                return {
                    label: 'Medium',
                    icon: Info,
                    classes: 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30',
                };
            case 'low':
                return {
                    label: 'Low',
                    icon: Info,
                    classes: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-500/30',
                };
            default:
                return {
                    label: 'Unknown',
                    icon: Info,
                    classes: 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-500/30',
                };
        }
    };

    const config = getPriorityConfig();
    const Icon = config.icon;

    return (
        <span className={`
            inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border
            ${config.classes}
            ${className}
        `}>
            {showIcon && <Icon className="w-3 h-3" />}
            {config.label}
        </span>
    );
};

interface StatusBadgeProps {
    status: StatusType;
    className?: string;
    showIcon?: boolean;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
                                                            status,
                                                            className = '',
                                                            showIcon = true
                                                        }) => {
    const getStatusConfig = () => {
        switch (status) {
            case 'resolved':
                return {
                    label: 'Resolved',
                    icon: CheckCircle2,
                    classes: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-500/30',
                };
            case 'investigating':
                return {
                    label: 'Investigating',
                    icon: Clock,
                    classes: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30',
                };
            case 'unresolved':
                return {
                    label: 'Unresolved',
                    icon: AlertCircle,
                    classes: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-500/30',
                };
            default:
                return {
                    label: 'Unknown',
                    icon: Info,
                    classes: 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-500/30',
                };
        }
    };

    const config = getStatusConfig();
    const Icon = config.icon;

    return (
        <span className={`
            inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border
            ${config.classes}
            ${className}
        `}>
            {showIcon && <Icon className="w-3 h-3" />}
            {config.label}
        </span>
    );
};

interface DataTypeBadgeProps {
    dataType: 'stealer' | 'combolist';
    className?: string;
}

export const DataTypeBadge: React.FC<DataTypeBadgeProps> = ({
                                                                dataType,
                                                                className = ''
                                                            }) => {
    const getDataTypeConfig = () => {
        switch (dataType) {
            case 'stealer':
                return {
                    label: 'Stealer',
                    classes: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-500/30',
                };
            case 'combolist':
                return {
                    label: 'Combolist',
                    classes: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-500/30',
                };
            default:
                return {
                    label: dataType,
                    classes: 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-500/30',
                };
        }
    };

    const config = getDataTypeConfig();

    return (
        <span className={`
            inline-flex items-center px-2 py-1 rounded text-xs font-medium border
            ${config.classes}
            ${className}
        `}>
            {config.label}
        </span>
    );
};